import React, { useState, useEffect } from 'react';
import { Menu, X, CalendarDays, ArrowUpRight, Trophy } from 'lucide-react';
import { siteConfig, freelancerStats } from '../data/mock';
import { Logo, TelegramIcon, WhatsAppIcon } from './Brand';

const navLinks = [
  { href: '#services', label: 'Services' },
  { href: '#process', label: 'Process' },
  { href: '#about', label: 'About' },
  { href: '#reviews', label: 'Reviews' },
  { href: '#contact', label: 'Contact' }
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);

  const go = (e, href) => {
    e.preventDefault();
    setOpen(false);
    setTimeout(() => document.querySelector(href)?.scrollIntoView({ behavior: 'smooth' }), 80);
  };

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 inset-x-0 z-[100] transition-all duration-500 ${
          scrolled || open ? 'bg-[#FAFAF7]/90 backdrop-blur-xl border-b border-[#E2E8F0]' : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex items-center justify-between h-[76px]">
            <a href="/" aria-label="OutreachByUsama home" data-testid="header-logo-link"><Logo /></a>

            <nav className="hidden lg:flex items-center gap-1" data-testid="desktop-nav">
              {navLinks.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className="px-4 py-2 text-sm font-medium text-slate hover:text-ink rounded-lg hover:bg-white transition-colors duration-200"
                >
                  {l.label}
                </a>
              ))}
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              <a
                href={siteConfig.freelancerProfile}
                target="_blank"
                rel="noopener noreferrer"
                className="pill pill-green !py-1.5 hover:brightness-95 transition"
                data-testid="header-top3-badge"
              >
                <Trophy className="w-3.5 h-3.5" /> {freelancerStats.rank} {freelancerStats.rankOf}
              </a>
              <a href={siteConfig.calendly} target="_blank" rel="noopener noreferrer" className="btn-ink !h-11 !px-5 text-sm" data-testid="header-book-call-button">
                <CalendarDays className="w-4 h-4" /> Book a call
              </a>
            </div>

            <button
              onClick={() => setOpen(!open)}
              aria-label="Toggle menu"
              data-testid="mobile-menu-toggle"
              className="lg:hidden w-11 h-11 rounded-xl bg-white border border-[#E2E8F0] flex items-center justify-center text-ink"
            >
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </header>

      {open && (
        <div className="lg:hidden fixed inset-0 z-[99] bg-paper pt-[76px]" data-testid="mobile-menu">
          <div className="h-full overflow-y-auto px-5 py-8 flex flex-col">
            <nav className="flex flex-col gap-2">
              {navLinks.map((l, i) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={(e) => go(e, l.href)}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                  className="fade-up flex items-center justify-between px-5 py-4 rounded-2xl bg-white border border-[#E2E8F0] text-lg font-display font-bold text-ink"
                  style={{ animationDelay: `${i * 60}ms` }}
                >
                  {l.label}
                  <ArrowUpRight className="w-5 h-5 text-green" />
                </a>
              ))}
            </nav>

            <a href={siteConfig.calendly} target="_blank" rel="noopener noreferrer" className="btn-ink mt-6 w-full" data-testid="mobile-book-call-button">
              <CalendarDays className="w-4 h-4" /> Book a 30-min call
            </a>

            <div className="mt-auto pt-8 grid grid-cols-2 gap-3">
              <a href={`https://wa.me/${siteConfig.whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="mobile-whatsapp-link" className="flex items-center justify-center gap-2 h-14 rounded-2xl bg-[#25D366] text-white font-semibold">
                <WhatsAppIcon /> WhatsApp
              </a>
              <a href={`https://t.me/${siteConfig.telegram}`} target="_blank" rel="noopener noreferrer" data-testid="mobile-telegram-link" className="flex items-center justify-center gap-2 h-14 rounded-2xl bg-[#229ED9] text-white font-semibold">
                <TelegramIcon /> Telegram
              </a>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;
